import React from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { SecureRoute } from "auth/SecureRoute";
import { AuthCompletedScreen } from "auth/AuthCompletedScreen";
import { InaScreen } from "inas/InaScreen";
import { PracticeReviewScreen } from "practice-reviews/PracticeReviewScreen";
import { UpcomingReviewNoticesScreen } from "scheduling/UpcomingReviewNoticesScreen";
import { ImportPRs } from "practice-reviews/ImportPrs";
import StatHolidaysScreen from "stat-holidays/StatHolidaysScreen";
import SystemEstimatedTimeRulesScreen from "./system-time-estimates/SystemEstimatedTimeRulesScreen";
import CustomReportsScreen from "custom-reports/CustomReportsScreen";
import TimedEventMarginsScreen from "timed-event-margins/TimedEventMarginsScreen";
import AdjustmentQuestionTemplatesScreen from "adjustment-question-templates/AdjustmentQuestionTemplatesScreen";
import EmailTemplatesScreen from "./email-templates/EmailTemplatesScreen";
import SignOffChecklistItemTemplatesScreen from "sign-off-checklist-items/SignOffChecklistItemTemplatesScreen";
import TimeConversionFormulasScreen from "time-conversion-formulas/TimeConversionFormulasScreen";
import RetentionPoliciesScreen from "retention-policies/RetentionPoliciesScreen";
import PdCoursesScreen from "./pd-courses/PdCoursesScreen";
import WorkflowInasScreen from "workflow-inas/WorkflowInasScreen";
import EngagementTypeTemplateScreen from "./engagement-type-templates/EngagementTypeTemplatesScreen";
import AdvancedPrSearchScreen from "./practice-reviews/AdvancedPrSearchScreen";
import { MasterChecklistScreen } from "master-checklist/MasterChecklistScreen";
import PersonalActivityLogScreen from "personal-activity-log/PersonalActivityLogScreen";
import CommitteeMeetingsScreen from "committee-meetings/CommitteeMeetingsScreen";
import CommitteeMeetingScreen from "committee-meetings/CommitteeMeetingScreen";
import CommitteeMembersScreen from "committee-members/CommitteeMembersScreen";
import AppRolesScreen from "app-roles/AppRolesScreen";
import { Permissions } from "users";
import MotionsScreen from "./motions/MotionsScreen";
import CommitteeDecisionsScreen from "./committee-meetings/CommitteeDecisionsScreen";
import ManageUsersScreen from "users/ManageUsersScreen";
import ExemptionLettersScreen from "exemptions/ExemptionLettersScreen";
import ActiveExemptionsScreen from "exemptions/ActiveExemptionsScreen";
import DecisionAdditionalParagraphsScreen from "decisions/DecisionAdditionalParagraphsScreen";
import DocumentTemplateSandboxScreen from "document-template-sandbox/DocumentTemplateSandboxScreen";
import PprpProgramsScreen from "pprp/PprpProgramsScreen";
import PprpProgramTasksScreen from "pprp/PprpProgramTasksScreen";
import RegistrationCommitteeItemScreen from "registration-committee-items/RegistrationCommitteeItemScreen";
import RegistrationCommitteeItemTypesScreen from "registration-committee-items/RegistrationCommitteeItemTypesScreen";
import { IAppConfig, useAppConfig } from "util/AppConfig";

export enum AppRoute {
  INAs = "/",
  PracticeReview = "/practice-review/:prNumber/:tab?",
  PracticeReviewClientChecklists = "/practice-review/:prNumber/client-checklists/:reviewedClientId?",
  PracticeReviewCandidateChecklists = "/practice-review/:prNumber/candidate-checklists/:reviewedClientId?",
  PracticeReviewDecision = "/practice-review/:prNumber/decision/:decisionTab?",
  GenerateUpcomingPafPracticeReviewNotices = "/generate-upcoming-review-notices",
  ApproveUpcomingPafPracticeReviewNotices = "/approve-upcoming-review-notices",
  GenerateUpcomingPprpProgramReviewNotices = "/generate-upcoming-program-review-notices",
  ApproveUpcomingPprpProgramReviewNotices = "/approve-upcoming-program-review-notices",
  PRCommitteeMeetings = "/committee-meetings",
  RegistrationCommitteeMeetings = "/registration-committee-meetings",
  PRCommitteeMeeting = "/committee-meetings/:id",
  RegistrationCommitteeMeeting = "/registration-committee-meetings/:id",
  PrcCommitteeMembers = "/prc-committee-members",
  RegistrationCommitteeMembers = "/registration-committee-members",
  AppAccessDetails = "/app-access",
  ImportPrs = "/import-prs",
  StatHolidays = "/stat-holidays",
  CustomReports = "/custom-reports",
  TimedEventMargins = "/timed-event-margins",
  Users = "/users",
  AdjustmentQuestionTemplates = "/adjustment-question-templates",
  EmailTemplates = "/email-templates",
  SignOffChecklistItems = "/sign-off-checklist-items",
  TimeConversionFormulas = "/time-conversion-formulas",
  RetentionPolicy = "/retention-policies",
  RetentionPolicies = "/retention-policies",
  PdCourses = "/pd-courses",
  Motions = "/motions",
  DecisionAdditionalPafParagraphs = "/additional-paragraphs-for-decisions",
  DecisionParagraphsForRegCom = "/decision-paragraphs-for-reg-com",
  WorkflowINAs = "/workflow-inas",
  ThemeTests = "/theme-tests",
  SystemEstimatedTimeRules = "/system-estimated-time-rules",
  AuthCompleted = "/authCompleted",
  EngagementTypeTemplates = "/engagement-type-templates",
  AdvancedPrSearch = "/advanced-pr-search",
  MasterChecklist = "/master-checklist",
  MyActivityLog = "/my-activity-log",
  EnterPafPracticeReviewCommitteeDecisions = "/enter-practice-review-committee-decisions",
  ApproveFinalPafPracticeReviewReports = "/approve-final-practice-review-reports",
  EnterPprpProgramReviewCommitteeDecisions = "/enter-program-review-committee-decisions", // TODO: if we use this screen for new Reg Com Committee Items, change this URL?
  ApproveFinalPprpProgramReviewReports = "/approve-final-program-review-reports", // TODO: if we use this screen for new Reg Com Committee Items, change this URL?
  ReviewPafPracticeExemptionLetters = "/review-exemption-letters",
  ApprovePafPracticeExemptionLetters = "/approve-exemption-letters",
  ReviewPprpProgramExemptionLetters = "/review-program-exemption-letters",
  ApprovePprpProgramExemptionLetters = "/approve-program-exemption-letters",
  Exemptions = "/active-exemptions",
  DocumentTemplateSandbox = "/document-template-sandbox",
  PprpPrograms = "/programs",
  PprpProgramTasks = "/program-tasks",
  RegistrationCommitteeItem = "/registration-committee-item/:id/:tab?",
  AddRegistrationCommitteeItem = "/registration-committee-item",
  RegistrationCommitteeItemDecision = "/registration-committee-item/:id/decision",
  RegistrationCommitteeItemTypes = "/registration-committee-item-types"
}

export interface NavCategory {
  categoryName: string;
  navItems?: (RouteConfig | DirectLink)[];
}

export interface RouteConfig {
  path: AppRoute;
  component: React.ComponentType<any>;
  authorizedPermissions?: Permissions[];
  navLabel?: string;
}

export interface DirectLink {
  href?: string;
  authorizedPermissions?: Permissions[];
  navLabel?: string;
}

export const getAdminRoutes = (appConfig: Partial<IAppConfig> | null): (NavCategory | RouteConfig)[] => [
  {
    categoryName: "Upcoming Notices",
    navItems: [
      {
        navLabel: "Generate Notices",
        path: AppRoute.GenerateUpcomingPafPracticeReviewNotices,
        component: () => <UpcomingReviewNoticesScreen mode="generate" />,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Approve Notices",
        path: AppRoute.ApproveUpcomingPafPracticeReviewNotices,
        component: () => <UpcomingReviewNoticesScreen mode="approve" />,
        authorizedPermissions: [Permissions.AdminTasks]
      }
    ]
  },
  {
    categoryName: "Committee Meetings",
    navItems: [
      {
        navLabel: "Meetings",
        path: AppRoute.PRCommitteeMeetings,
        component: CommitteeMeetingsScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Enter Decisions",
        path: AppRoute.EnterPafPracticeReviewCommitteeDecisions,
        component: () => <CommitteeDecisionsScreen mode="enter-committee-decisions" />,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Review Final Reports",
        path: AppRoute.ApproveFinalPafPracticeReviewReports,
        component: () => <CommitteeDecisionsScreen mode="review-final-reports" />,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Members",
        path: AppRoute.PrcCommitteeMembers,
        component: CommitteeMembersScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      }
    ]
  },
  {
    categoryName: "Exemptions",
    navItems: [
      {
        navLabel: "Active Exemptions",
        path: AppRoute.Exemptions,
        component: ActiveExemptionsScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Review Exemption Letters",
        path: AppRoute.ReviewPafPracticeExemptionLetters,
        component: () => <ExemptionLettersScreen mode="review" />,
        authorizedPermissions: [Permissions.ExemptionReviewLetter]
      },
      {
        navLabel: "Approve Exemption Letters",
        path: AppRoute.ApprovePafPracticeExemptionLetters,
        component: () => <ExemptionLettersScreen mode="approve" />,
        authorizedPermissions: [Permissions.ExemptionConfirm]
      }
    ]
  },
  {
    navLabel: "Import Reviews",
    path: AppRoute.ImportPrs,
    component: ImportPRs,
    authorizedPermissions: [Permissions.AdminTasks]
  },
  {
    categoryName: "Pre-Approved Programs",
    navItems: [
      {
        navLabel: "Programs",
        path: AppRoute.PprpPrograms,
        component: PprpProgramsScreen,
        authorizedPermissions: [Permissions.AdminTasksPprp]
      },
      {
        navLabel: "Program Tasks",
        path: AppRoute.PprpProgramTasks,
        component: PprpProgramTasksScreen,
        authorizedPermissions: [Permissions.AdminTasksPprp]
      },
      {
        navLabel: "Generate Notices",
        path: AppRoute.GenerateUpcomingPprpProgramReviewNotices,
        component: () => <UpcomingReviewNoticesScreen mode="generate" pprpProgramReviews />,
        authorizedPermissions: [Permissions.AdminTasksPprp]
      },
      {
        navLabel: "Approve Notices",
        path: AppRoute.ApproveUpcomingPprpProgramReviewNotices,
        component: () => <UpcomingReviewNoticesScreen mode="approve" pprpProgramReviews />,
        authorizedPermissions: [Permissions.AdminTasksPprp]
      },
      {
        navLabel: "Review Exemption Letters",
        path: AppRoute.ReviewPprpProgramExemptionLetters,
        component: () => <ExemptionLettersScreen mode="review" onPprpProgramReviews />,
        authorizedPermissions: [Permissions.ExemptionReviewLetter]
      },
      {
        navLabel: "Approve Exemption Letters",
        path: AppRoute.ApprovePprpProgramExemptionLetters,
        component: () => <ExemptionLettersScreen mode="approve" onPprpProgramReviews />,
        authorizedPermissions: [Permissions.ExemptionConfirm]
      }
    ]
  },
  {
    categoryName: "Registration Committee",
    navItems: [
      {
        navLabel: "Add Committee Item",
        path: AppRoute.AddRegistrationCommitteeItem,
        component: RegistrationCommitteeItemScreen,
        authorizedPermissions: [
          Permissions.AdminTasks,
          Permissions.AdminTasksPprp,
          Permissions.RegComItemSendForApproval,
          Permissions.RegComItemApproveForMeeting,
          Permissions.RegComItemDecisionLetterFinalApproval,
          Permissions.RegComItemCreateOnly
        ]
      },
      {
        navLabel: "Meetings",
        path: AppRoute.RegistrationCommitteeMeetings,
        component: () => <CommitteeMeetingsScreen registrationCommittees />,
        authorizedPermissions: [
          Permissions.AdminTasksPprp,
          Permissions.MeetingBinderAssemble,
          Permissions.MeetingBinderGenerate,
          Permissions.MeetingsRegComEdit
        ]
      },
      {
        navLabel: "Enter Decisions",
        path: AppRoute.EnterPprpProgramReviewCommitteeDecisions,
        component: () => <CommitteeDecisionsScreen mode="enter-committee-decisions" registrationCommittees />,
        authorizedPermissions: [Permissions.AdminTasksPprp]
      },
      {
        navLabel: "Review Final Decisions",
        path: AppRoute.ApproveFinalPprpProgramReviewReports,
        component: () => <CommitteeDecisionsScreen mode="review-final-reports" registrationCommittees />,
        authorizedPermissions: [
          Permissions.AdminTasksPprp,
          Permissions.RegComItemDecisionLetterRegistrarReview,
          Permissions.RegComItemDecisionLetterChairReview,
          Permissions.RegComItemDecisionLetterFinalApproval
        ]
      },
      {
        navLabel: "Registration Committee Members",
        path: AppRoute.RegistrationCommitteeMembers,
        component: () => <CommitteeMembersScreen registrationCommittee />,
        authorizedPermissions: [Permissions.AdminTasksPprp]
      },
      {
        navLabel: "Registration Committee Item Types",
        path: AppRoute.RegistrationCommitteeItemTypes,
        component: RegistrationCommitteeItemTypesScreen,
        authorizedPermissions: [Permissions.AdminTasksPprp]
      },
      {
        navLabel: "Paragraphs for Item Decisions",
        path: AppRoute.DecisionParagraphsForRegCom,
        component: () => <DecisionAdditionalParagraphsScreen forRegistrationCommitteeItems />,
        authorizedPermissions: [Permissions.TemplatesRegComParagraphsEdit]
      }
    ]
  },
  {
    categoryName: "Templates",
    navItems: [
      {
        navLabel: "Additional Paragraphs for Decisions",
        path: AppRoute.DecisionAdditionalPafParagraphs,
        component: DecisionAdditionalParagraphsScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Adjustment Question Templates",
        path: AppRoute.AdjustmentQuestionTemplates,
        component: AdjustmentQuestionTemplatesScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Document Template Sandbox",
        path: AppRoute.DocumentTemplateSandbox,
        component: DocumentTemplateSandboxScreen,
        authorizedPermissions: [Permissions.DocumentTemplateSandboxAllow]
      },
      {
        navLabel: "Document Template Storage",
        href: appConfig?.sharepointDocumentTemplateLibraryUrl,
        authorizedPermissions: [Permissions.AdminTasks, Permissions.AdminTasksPprp]
      },
      {
        navLabel: "Email Templates",
        path: AppRoute.EmailTemplates,
        component: EmailTemplatesScreen,
        authorizedPermissions: [Permissions.TemplatesEmailEdit]
      },
      {
        navLabel: "Engagement Type Templates",
        path: AppRoute.EngagementTypeTemplates,
        component: EngagementTypeTemplateScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Master Checklist",
        path: AppRoute.MasterChecklist,
        component: MasterChecklistScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Motions",
        path: AppRoute.Motions,
        component: MotionsScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Sign-off Checklist Items",
        path: AppRoute.SignOffChecklistItems,
        component: SignOffChecklistItemTemplatesScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      }
    ]
  },
  {
    categoryName: "System",
    navItems: [
      {
        navLabel: "App Access Details",
        path: AppRoute.AppAccessDetails,
        component: AppRolesScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Custom Reports",
        path: AppRoute.CustomReports,
        component: CustomReportsScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Non-working Days",
        path: AppRoute.StatHolidays,
        component: StatHolidaysScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "PD Courses",
        path: AppRoute.PdCourses,
        component: PdCoursesScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Retention Policies",
        path: AppRoute.RetentionPolicies,
        component: RetentionPoliciesScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "System Estimated Time Rules",
        path: AppRoute.SystemEstimatedTimeRules,
        component: SystemEstimatedTimeRulesScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Time Conversion Formulas",
        path: AppRoute.TimeConversionFormulas,
        component: TimeConversionFormulasScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Timed Event Margins",
        path: AppRoute.TimedEventMargins,
        component: TimedEventMarginsScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Users",
        path: AppRoute.Users,
        component: ManageUsersScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      },
      {
        navLabel: "Workflow INAs",
        path: AppRoute.WorkflowINAs,
        component: WorkflowInasScreen,
        authorizedPermissions: [Permissions.AdminTasks]
      }
    ]
  }
];

const flattenToSecureRoute = (routes: (NavCategory | RouteConfig | DirectLink)[]) =>
  routes
    .flatMap((routeOrCategory) =>
      (routeOrCategory as NavCategory).navItems ? (routeOrCategory as NavCategory).navItems! : [routeOrCategory]
    )
    .map((routeOrLink) => {
      if ("href" in routeOrLink) {
        return null;
      }
      const route = routeOrLink as RouteConfig;
      return <SecureRoute exact path={route.path} component={route.component} authorizedPermissions={route.authorizedPermissions} />;
    });

export const Routes: React.FunctionComponent = () => {
  const appConfig = useAppConfig();
  return (
    <Switch>
      <Route exact path={AppRoute.AuthCompleted} component={AuthCompletedScreen} />
      <SecureRoute
        exact
        path={[
          AppRoute.PracticeReviewClientChecklists,
          AppRoute.PracticeReviewCandidateChecklists,
          AppRoute.PracticeReviewDecision,
          AppRoute.PracticeReview
        ]}
        component={PracticeReviewScreen}
      />

      <SecureRoute exact path={AppRoute.INAs} component={InaScreen} />
      <SecureRoute exact path={AppRoute.AdvancedPrSearch} component={AdvancedPrSearchScreen} />
      <SecureRoute exact path={AppRoute.MyActivityLog} component={PersonalActivityLogScreen} />

      {flattenToSecureRoute(getAdminRoutes(appConfig))}

      <SecureRoute
        exact
        path={AppRoute.PRCommitteeMeeting}
        component={CommitteeMeetingScreen}
        authorizedPermissions={[Permissions.AdminTasks]}
      />
      <SecureRoute
        exact
        path={AppRoute.RegistrationCommitteeMeeting}
        component={CommitteeMeetingScreen}
        authorizedPermissions={[
          Permissions.AdminTasksPprp,
          Permissions.MeetingBinderAssemble,
          Permissions.MeetingBinderGenerate,
          Permissions.RegComItemSendForApproval,
          Permissions.RegComItemApproveForMeeting,
          Permissions.RegComItemDecisionLetterFinalApproval
        ]}
      />

      {/* This is one of the few SecureRoutes that is not a single AppRoute path; why? */}
      <SecureRoute
        exact
        path={[AppRoute.RegistrationCommitteeItem, AppRoute.AddRegistrationCommitteeItem, AppRoute.RegistrationCommitteeItemDecision]}
        component={RegistrationCommitteeItemScreen}
      />

      <Redirect to={AppRoute.INAs} />
    </Switch>
  );
};
